import UpcomingWeddingsTable from './UpcomingWeddingsTable.jsx'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LeaveKazooPopup } from '../../popups/LeaveKazooPopup.jsx'
import { DeleteWeddingPopup } from '../../popups/DeleteWeddingPopup.jsx'
import {
	getKazooDashboard,
	getKazooMembers,
	getKazoos,
	fetchKazooDetailsNew,
	getPlannerKazoos,
} from '../../../features/kazooEventSlice.js'
import { getVendorDashboard } from '../../../features/vendorSlice.js'
import ButtonCustom from '../../atoms/button/ButtonCustom.jsx'
import { Scheduler } from '../../scheduler/Scheduler.jsx'
import {
	getEvents,
	getVendorEvents,
	setSelectedUsers,
} from '../../../features/schedulerSlice.js'

import { Box, Grid, Typography, Card } from '@mui/material'
import LinearProgress, {
	linearProgressClasses,
} from '@mui/material/LinearProgress'
import WeddingCard from '../../atoms/wedding-card/WeddingCard.jsx'
import { PieChart, useDrawingArea, LineChart } from '@mui/x-charts'
import styles from './WeddingPlannerDashboard.module.scss'
import { MetricCards } from './MetricCard.jsx'
import { getAllProposals } from '../../../features/proposalSlice.js'
import { setSettingsTab } from '../../../features/authSlice.js'
import { formatCommonAmount } from '../../../util/formatAmount.js'
import { getVendorPackages } from '../../../features/packageSlice.js'

function CommonHome() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { kazooId } = useParams()

	const [popupToggle, setPopupToggle] = useState(false)
	const [dashboardData, setDashboardData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	let isMounted = false
	const user = useSelector((state) => state.auth.user)
	const kazooMembers = useSelector((state) => state.kazooEvent.kazooMembers)
	const accountType = useSelector((state) => state.auth.accountType)
	const vendorEvents = useSelector((state) => state.scheduler.vendorEvents)
	const vendorTasks = useSelector((state) => state.scheduler.vendorTasks)

	const urlType = () => {
		switch (accountType) {
			case 'vendor':
				return 'vendor'
			case 'kazoo-planner':
				return 'planner'
			default:
				return ''
		}
	}

	const calculateProfileStepFromUser = useCallback((u) => {
		if (!u) return 0
		if (u.businessTypes?.length > 0 || !!u.customBusinessType?.trim()) {
			if (u.businessDescription) {
				if (u.numEmployees) {
					if (u.services && u.services.length > 0) {
						if (u.maxCapacity) {
							if (u.media && u.media.length > 0) {
								return 7
							}
							return 6
						}
						return 5
					}
					return 4
				}
				return 3
			}
			return 2
		}
		return 0
	}, [])

	// Memoize the toggle function
	const togglePopup = useCallback(() => {
		setPopupToggle((prev) => !prev)
	}, [])

	const bride = kazooMembers.bride
	const groom = kazooMembers.groom

	const profileStep = useMemo(
		() => calculateProfileStepFromUser(user),
		[user, calculateProfileStepFromUser]
	)
	const isProfileComplete = !!user?.profileCompleted
	const profileProgress = isProfileComplete
		? 100
		: Math.min(Math.max((profileStep - 1) * (100 / 7), 0), 100)

	const eventSettings = useMemo(() => ({
		allowAdding: false,
		allowEditing: false,
		allowDeleting: false,
		allowResizing: false,
		allowDrag: false,
		dataSource: [...vendorEvents, ...vendorTasks],
	}), [vendorEvents, vendorTasks])

	const kazoos = useSelector((s) => s.kazooEvent.kazoos)
	const tasks = useSelector((s) => s.scheduler.vendorTasks)
	const proposals = useSelector((s) => s.proposal.proposals)

	const numKazoos = useMemo(() => (kazoos || []).length, [kazoos])
	const numTasks = useMemo(() => (tasks || []).length, [tasks])
	const numProposals = useMemo(() => (proposals || []).length, [proposals])
	const [revenueData, setRevenueData] = useState([])
	const [packageTotal, setPackageTotal] = useState(0)
	const [packageDetailsChartdata, setPackageDetailsChartdata] = useState([])

	// Memoize the formatted wedding rows
	const upcomingWeddingsRows = useMemo(() => {
		return (kazoos || []).slice(0, 4).map((kazoo) => {
			const budgetLabel =
				typeof kazoo?.budget === 'number'
					? `$${kazoo.budget.toLocaleString()}`
					: 'TBD'

			return {
				id: kazoo?._id,
				Couple: kazoo.kazooName,
				Date: kazoo?.date ? kazoo.date.slice(0, 10) : 'TBD',
				Budget: budgetLabel,
				Status: 'Upcoming',
				avatar: kazoo?.image,
			}
		})
	}, [kazoos])

	// Memoize the metric cards data
	useEffect(() => {
		isMounted = true
		const fetchData = async () => {
			if (!isMounted) return
			if (!['vendor', 'kazoo-planner'].includes(accountType)) return
			try {
				setLoading(true)
				const promises = [
					dispatch(getVendorEvents('isVendorTasks')),
					dispatch(getAllProposals()),
				]

				if (accountType === 'vendor') {
					promises.push(dispatch(getKazoos()))
					promises.push(dispatch(getVendorEvents()))
					promises.push(dispatch(getVendorDashboard()))
					promises.push(dispatch(getVendorPackages()))
				} else if (accountType === 'kazoo-planner') {
					promises.push(dispatch(getPlannerKazoos()))
				}
				const [
					eventsRes,
					proposalsRes,
					kazoosRes,
					tasksRes,
					dashboard,
					vendorPackagesRes,
				] = await Promise.all(promises)
				if (isMounted && dashboard?.payload?.success) {
					setDashboardData(dashboard.payload.data)
					// Format the monthly revenue data for the chart
					if (dashboard.payload.data.monthlyRevenue) {
						const formattedData =
							dashboard.payload.data.monthlyRevenue.map(
								(item) => ({
									month: item.month,
									revenue: item.revenue,
								})
							)
						setRevenueData(formattedData)
					}
				}
				if (isMounted && vendorPackagesRes?.payload?.success) {
					const { packages } = vendorPackagesRes.payload.data
					const colors = ['#E8F4FD', '#B3D9FF', '#7CA6BD', '#4A90E2']
					const packageDetailsChartdata = packages.map(
						(pkg, index) => ({
							value: pkg.usedCount,
							label: pkg.name,
							price: formatCommonAmount(pkg.price),
							color: colors[index % colors.length],
							button: `${pkg.usedCount} bookings`,
						})
					)
					setPackageTotal(
						packages.reduce(
							(sum, pkg) => sum + (pkg.usedCount || 0),
							0
						)
					)
					setPackageDetailsChartdata(packageDetailsChartdata)
				}
			} catch (err) {
				console.error('Error fetching dashboard data:', err)
				if (isMounted) {
					setError('Failed to load dashboard data')
				}
			} finally {
				if (isMounted) {
					setLoading(false)
				}
			}
		}

		fetchData()

		return () => {
			isMounted = false
		}
	}, [dispatch, kazooId, bride?._id, groom?._id, accountType])

	const totalRevenue = useMemo(() => {
		if (!proposals || !proposals.length) return 0

		return proposals
			.filter(
				(p) => String(p.paymentStatus || '').toLowerCase() === 'paid'
			)
			.reduce((sum, p) => {
				const amount = Number(p.bidAmount) || 0
				return sum + amount
			}, 0)
	}, [proposals])

	const monthlyRevenueData = useMemo(() => {
		if (!Array.isArray(proposals) || !proposals.length) return []

		const paid = proposals.filter(
			(p) => String(p.status || '').toLowerCase() === 'approved'
		)

		const buckets = new Map() // key: "YYYY-M" → total revenue

		for (const p of paid) {
			if (!p.paymentDueDate) continue

			const d = new Date(p.paymentDueDate)
			if (Number.isNaN(d.getTime())) continue

			const year = d.getFullYear()
			const monthIdx = d.getMonth()
			const key = `${year}-${monthIdx}`

			const prev = buckets.get(key) || 0
			buckets.set(key, prev + (Number(p.bidAmount) || 0))
		}

		const now = new Date()
		const startYear = now.getFullYear()
		const startMonth = now.getMonth()

		const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'short' })
		const result = []

		for (let offset = 0; offset < 6; offset++) {
			const d = new Date(startYear, startMonth + offset, 1)
			const year = d.getFullYear()
			const monthIdx = d.getMonth()
			const key = `${year}-${monthIdx}`

			const revenue = buckets.get(key) || 0

			result.push({
				month: `${monthFmt.format(d)} ${year}`,
				revenue,
			})
		}

		return result
	}, [proposals])

	const metricCardsData = useMemo(
		() => [
			{
				label: 'Total Weddings',
				value: dashboardData?.totalWeddings || numKazoos,
				icon: '/images/lovely.svg',
				bgColor: '#E8F2FB',
				changeColor: 'var(--success)',
			},
			{
				label: 'Active Proposals',
				value: dashboardData?.activeProposals || numProposals,
				// change: '+2 from last month',
				icon: '/images/proposal-icon.svg',
				bgColor: '#E8F2FB',
				changeColor: 'var(--success)',
			},
			{
				label: 'Total Revenue',
				value: `$${totalRevenue.toLocaleString()}`,
				change:
					totalRevenue > 0
						? 'Based on paid proposals'
						: 'No paid proposals yet',
				icon: '/images/dollar-circle.svg',
				bgColor: '#E8F2FB',
				changeColor:
					totalRevenue > 0 ? 'var(--success)' : 'var(--error)',
			},
			{
				label: 'Tasks',
				value: numTasks,
				// change: '5 overdue',
				icon: '/images/task-square.svg',
				bgColor: '#FEF5F5',
				changeColor: 'var(--error)',
			},
		],
		[dashboardData, numKazoos, numProposals, numTasks]
	)

	const recentProposalsRows = useMemo(() => {
		if (!Array.isArray(proposals)) return []

		const list = proposals.map((p, index) => {
			const kazoo = (kazoos || []).find(
				(k) => String(k._id) === String(p.kazooId)
			)

			const rawDate = p.paymentDueDate
			const formattedDate = rawDate ? String(rawDate).slice(0, 10) : 'TBD'

			const numericBudget =
				typeof p.userBudget === 'number'
					? p.userBudget
					: typeof kazoo?.budget === 'number'
						? kazoo.budget
						: typeof p.budget === 'number'
							? p.budget
							: typeof p.quote === 'number'
								? p.quote
								: null

			const budgetLabel =
				numericBudget != null
					? formatCommonAmount(numericBudget)
					: 'TBD'

			const statusLabel = p.status || 'New'

			const avatar = p.brideProfilePic || p.groomProfilePic

			return {
				id: p._id || p.id || p.proposalId || `proposal-row-${index}`,
				Couple: p.kazooName,
				Date: formattedDate,
				Budget: budgetLabel,
				Status: statusLabel,
				avatar,
			}
		})
		return list
			.filter((item) => item.Date !== 'TBD' && item.Budget !== 'TBD')
			.slice(0, 4)
	}, [proposals, kazoos])

	const formatToday = () => {
		const today = new Date()
		return today
			.toLocaleDateString('en-GB', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
			.replace(/,/g, '')
	}
	return (
		<>
			<div className={styles.pageHeader}>
				<Typography component="h1"> Home </Typography>
				<div className={styles.right}>
					<div className={`${styles.date}`}>
						<img alt="" src="/images/calendar_line.svg" />
						<span>{formatToday()}</span>
					</div>
				</div>
			</div>
			<div className={styles.weddingDashboard}>
				{!isProfileComplete && (
					<Box mb={3}>
						<Card
							variant="outlined"
							className={styles.businessProfileProgressCard}
						>
							<Typography
								variant="h6"
								className={styles.businessProfileProgressTitle}
							>
								Complete your business profile
							</Typography>
							<LinearProgress
								variant="determinate"
								value={profileProgress}
								sx={{
									padding: '0.5rem',
									borderRadius: '1rem',
									backgroundColor: '#DFE4EB',
									[`& .${linearProgressClasses.bar}`]: {
										backgroundColor: '#7CA6BD',
										borderRadius: '50px',
									},
								}}
							/>
							<Box
								mt={1.5}
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								gap={2}
								flexWrap="wrap"
							>
								<Typography
									variant="h6"
									className={
										styles.businessProfileProgressPercentage
									}
								>
									{Math.round(profileProgress)}% complete
								</Typography>
								<ButtonCustom
									variant="default"
									onClick={() => {
										dispatch(setSettingsTab(5))
										navigate('/settings')
									}}
								>
									{profileStep <= 1
										? 'Get Started →'
										: 'Continue →'}
								</ButtonCustom>
							</Box>
						</Card>
					</Box>
				)}
				<MetricCards cards={metricCardsData} />
				{/* <Typography variant='h5'>Launching Monday November 17th</Typography> */}
				<>
					<div className={styles.weddingCardsWrp}>
						<Grid container spacing={2}>
							{/* TODO: IMPLEMENT LATER */}
							<Grid size={{ xs: 12, md: 6 }}>
								<WeddingCard
									heading="Upcoming Weddings"
									actionLabel="View All"
									handleAction={() =>
										navigate(`/${urlType()}-weddings`)
									}
								>
									<UpcomingWeddingsTable
										rows={upcomingWeddingsRows}
									/>
								</WeddingCard>
							</Grid>
							{/* TODO: IMPLEMENT LATER */}
							<Grid size={{ xs: 12, md: 6 }}>
								<WeddingCard heading="Revenue Overview">
									<Box sx={{ width: '100%', height: 300 }}>
										<LineChart
											xAxis={[
												{
													dataKey: 'month',
													scaleType: 'point',
												},
											]}
											series={[
												{
													dataKey: 'revenue',
													label: 'Revenue',
													showMark: true,
													area: true,
													curve: 'monotone',
													color: '#7CA6BD',
												},
											]}
											dataset={monthlyRevenueData}
											grid={{
												horizontal: true,
												vertical: false,
											}}
											margin={{
												top: 20,
												right: 20,
												bottom: 20,
												left: 40,
											}}
											slotProps={{
												legend: { hidden: true },
												tooltip: {
													trigger: 'axis',
													labelFormatter: (item) =>
														`${item.data.month}\nRevenue: $${Number(
															item.data.revenue ||
																0
														).toLocaleString()}`,
												},
											}}
											sx={{
												'& .MuiLineElement-root': {
													strokeWidth: 2,
												},
												'& .MuiAreaElement-root': {
													fill: 'url(#revenueGradient)',
													opacity: 0.5,
												},
												'& .MuiMarkElement-root': {
													fill: '#7CA6BD',
													r: 3,
												},
											}}
										>
											<defs>
												<linearGradient
													id="revenueGradient"
													x1="0"
													y1="0"
													x2="0"
													y2="1"
												>
													<stop
														offset="5%"
														stopColor="#7CA6BD"
														stopOpacity={0.3}
													/>
													<stop
														offset="95%"
														stopColor="#7CA6BD"
														stopOpacity={0}
													/>
												</linearGradient>
											</defs>
										</LineChart>
									</Box>
								</WeddingCard>
							</Grid>
							{/* TODO: implement later */}
							<Grid size={{ xs: 12, md: 6 }}>
								<WeddingCard
									heading="Recent Proposals"
									actionLabel="View All"
									handleAction={() =>
										navigate(`/${urlType()}-proposal`)
									}
								>
									<UpcomingWeddingsTable
										rows={recentProposalsRows}
										dateLabel="Due Date"
									/>
								</WeddingCard>
							</Grid>
							{/* TODO: IMPLEMENT LATER */}
							<Grid size={{ xs: 12, md: 6 }}>
								<WeddingCard
									heading="Package Details"
									actionLabel="View Details"
									handleAction={() => navigate('/settings')}
								>
									<div
										className={styles.budgetRow}
										style={{ width: '100%' }}
									>
										<Box
											sx={{
												width: '100%',
												display: 'flex',
												flexDirection: {
													xs: 'column',
													sm: 'row',
												},
												alignItems: 'center',
												gap: 4,
												justifyContent: 'space-between',
											}}
										>
											<Box className={styles.chartWrp}>
												<PieChart
													series={[
														{
															data: packageDetailsChartdata.map(
																(item) => ({
																	id: item.id,
																	value: item.value,
																	label: item.label,
																	color: item.color,
																})
															),
															innerRadius: 0,
															outerRadius: 80,
															valueFormatter: ({
																value,
															}) =>
																`\n${value} bookings (${((value * 100) / packageTotal).toFixed(2)}%)`,
														},
													]}
													height={200}
													sx={{
														'.MuiChartsLegend-vertical':
															{ display: 'none' },
													}}
												/>
											</Box>

											<Box
												sx={{
													flex: 1,
													display: 'flex',
													flexDirection: 'column',
													gap: '20px',
													width: '100%',
												}}
											>
												{packageDetailsChartdata?.map(
													(item, i) => (
														<Box
															key={`package-details-${item.id}-${i}`}
															sx={{
																borderLeft: `4px solid ${item.color}`,
																pl: '8px',
																display: 'flex',
																alignItems:
																	'center',
																justifyContent:
																	'space-between',
															}}
														>
															<Box>
																<Typography
																	variant="subtitle1"
																	fontWeight={
																		500
																	}
																>
																	{item.label}
																</Typography>
																<Typography
																	variant="body2"
																	color="text.secondary"
																>
																	Starting at{' '}
																	{item.price}
																</Typography>
															</Box>
															<ButtonCustom
																variant="secondary"
																size="sm"
																style={{
																	color: 'var(--brand)',
																}}
															>
																{item.button}
															</ButtonCustom>
														</Box>
													)
												)}
											</Box>
										</Box>
									</div>
								</WeddingCard>
							</Grid>
						</Grid>
					</div>
				</>

				<div className={styles.yourWeddingDayTimeline}>
					<div className={styles.calendarHeading}>
						<h2 className={styles.weddingDashboardHeading}>
							Events Timeline
						</h2>
					</div>

					<div
						className={styles.calendarHeading}
						style={{ marginTop: '30px' }}
					>
						 <div className={styles.schedulerWrap}>
							<div className="calendarWrp commonCalendar ">
								<div className="calendarInner">
									<Scheduler
                                        disableWeddingDateButton={true}
										eventSettings={eventSettings}
										partyMembers={[{ Id: user._id, OwnerText: '' }]}
										allowResizing={false}
										allowDragAndDrop={false}
										height="77dvh"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default CommonHome
